import { createEffect, createMemo, createSignal, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { type KeyLabelRefusal, keyLabelRefusalOf, setKeyLabel } from '../lib/key-label-rename';
import type { KeyRow } from '../lib/keys-types';
import { Field } from './Field';
import { Select, type SelectOption } from './Select';
import { buttonClass } from './ui/Button';

// The Advanced drawer's "use a specific key" field (#5211, epic #5200 slice 03) —
// a route's `key_label` pin, offered from LABELLED keys only (an unlabelled key
// has no name a route can select it by, `pinTriggerValue`'s own rule), plus an
// inline rename of the SELECTED key via `PATCH /v1/billing/keys/:id/label`
// (`lib/key-label-rename.ts`). Renaming moves every pin naming the old label —
// including this purpose's, if it is the one pinned — server-side, in one
// transaction (`key-label.ts`), so the caller re-reads both keys and routes
// after a rename rather than re-pinning.
//
// The rename write is OWNED here, not lifted to the container: it is a single
// key's own mutation with no cross-card race to guard (unlike `ModelSaves`'
// per-purpose locks), so a local signal is the whole story. `onRenamed` is the
// caller's re-read trigger — the label may have moved this purpose's own pin.

/** The server's own reason, inline — never a generic message papering over it. */
function refusalText(refusal: KeyLabelRefusal): string {
  switch (refusal.kind) {
    case 'label_taken':
      return t('agents.drawer.key.label_taken');
    case 'label_in_use':
      return refusal.detail;
    case 'label_pinned':
      return t('agents.drawer.key.label_pinned', { n: refusal.routes });
    case 'refused':
      return refusal.detail ?? t('agents.drawer.key.rename_failed');
  }
}

export type AgentKeyLabelFieldProps = {
  readonly idPrefix: string;
  /** Every ORG key, so the rename target's id resolves from its current label. */
  readonly keys: readonly KeyRow[];
  /** The route's current `key_label` pin, or null (no key pinned). */
  readonly value: string | null;
  readonly canEdit: boolean;
  /** The route write (the pin itself) is in flight. */
  readonly busy: boolean;
  readonly onChoose: (label: string | null) => void;
  readonly token: string | null;
  /** Re-read after a rename lands — it may have moved this purpose's own pin
   * (or any other), server-side, in the same transaction. */
  readonly onRenamed: () => void;
};

export function AgentKeyLabelField(props: AgentKeyLabelFieldProps): JSX.Element {
  const labelled = (): readonly KeyRow[] => props.keys.filter((k) => k.label !== null);
  const selectedKey = (): KeyRow | undefined => labelled().find((k) => k.label === props.value);
  // '' is a real, listed option here too (see RouteEffortField): the unset
  // pin stays "Automatic", UNCHANGED by this slice (only its migration onto
  // Select, step 3 of the issue's own TDD plan — the effort field's "Model
  // default" copy change does not apply to this KEY picker).
  const keyOptions = createMemo((): readonly SelectOption<string>[] => [
    { value: '', label: t('agents.drawer.key.none') },
    ...labelled().map((key) => ({ value: key.label as string, label: key.label as string })),
  ]);

  const [draft, setDraft] = createSignal('');
  const [renameBusy, setRenameBusy] = createSignal(false);
  const [renameError, setRenameError] = createSignal<KeyLabelRefusal | null>(null);
  // Seed the rename draft from the selection — a legit init effect
  // (`ModelPicker` does the same), not a sync of derived state.
  createEffect(
    on(selectedKey, (key) => {
      setDraft(key?.label ?? '');
      setRenameError(null);
    }),
  );

  const submitRename = (e: Event): void => {
    e.preventDefault();
    const key = selectedKey();
    const next = draft().trim();
    if (key === undefined || next === '' || next === key.label) return;
    setRenameBusy(true);
    setRenameError(null);
    setKeyLabel(key.id, next, props.token)
      .then(() => props.onRenamed())
      .catch((err: unknown) => setRenameError(keyLabelRefusalOf(err)))
      .finally(() => setRenameBusy(false));
  };

  return (
    <div class="field-group">
      <Field label={t('agents.drawer.key.label')} id={`${props.idPrefix}-key`}>
        {(a11y) => (
          <Select
            a11y={a11y}
            label={t('agents.drawer.key.label')}
            value={props.value ?? ''}
            options={keyOptions()}
            disabled={!props.canEdit || props.busy}
            onChange={(next) => props.onChoose(next === '' ? null : next)}
            block
          />
        )}
      </Field>

      <Show when={selectedKey()}>
        {(key) => (
          <form class="agent-drawer__rename" onSubmit={submitRename}>
            <Field
              label={t('agents.drawer.key.rename_label')}
              id={`${props.idPrefix}-key-rename`}
              error={renameError() ? refusalText(renameError() as KeyLabelRefusal) : null}
            >
              {(a11y) => (
                <input
                  {...a11y}
                  class="input"
                  value={draft()}
                  disabled={!props.canEdit || renameBusy()}
                  onInput={(e) => {
                    setRenameError(null);
                    setDraft(e.currentTarget.value);
                  }}
                />
              )}
            </Field>
            <button
              type="submit"
              class={buttonClass({ variant: 'outline', size: 'sm' })}
              disabled={
                !props.canEdit ||
                renameBusy() ||
                draft().trim() === '' ||
                draft().trim() === key().label
              }
            >
              {renameBusy() ? t('agents.drawer.key.renaming') : t('agents.drawer.key.rename')}
            </button>
          </form>
        )}
      </Show>
    </div>
  );
}
